// Uclean-Admin-Dashboard/src/mocks/promo/discountRules/handlers.js
// Discount-rule CRUD with structural conditions-tree validation.
// Spec: docs/admin-handover.md §2.1, §7.1, §8.

import { http, HttpResponse } from 'msw';
import { BASE } from '../_shared/base';
import { paginate } from '../_shared/pagination';
import { engineError } from '../_shared/errors';
import { rulesStore } from './store';

const ROOT = `${BASE}/discount-rules`;
const ACTION_TYPES = ['percentage_off_subtotal', 'fixed_off_subtotal'];

// Returns null on valid; returns a JSON-pointer path string on first failure.
function validateConditions(conditions) {
  if (!conditions || typeof conditions !== 'object') return '/conditions';
  const branches = Object.keys(conditions);
  if (branches.length !== 1 || (branches[0] !== 'all' && branches[0] !== 'any')) {
    return '/conditions';
  }
  const branchKey = branches[0];
  const leaves = conditions[branchKey];
  if (!Array.isArray(leaves) || leaves.length === 0) {
    return `/conditions/${branchKey}`;
  }
  for (let i = 0; i < leaves.length; i += 1) {
    const leaf = leaves[i];
    if (!leaf || typeof leaf !== 'object') return `/conditions/${branchKey}/${i}`;
    if (!('fact' in leaf)) return `/conditions/${branchKey}/${i}/fact`;
    if (!('op' in leaf)) return `/conditions/${branchKey}/${i}/op`;
    if (!('value' in leaf)) return `/conditions/${branchKey}/${i}/value`;
  }
  return null;
}

function validateAction(action) {
  if (!action || typeof action !== 'object') return '/action';
  if (!ACTION_TYPES.includes(action.type)) return '/action/type';
  if (typeof action.value !== 'number') return '/action/value';
  return null;
}

function validateRuleBody(body) {
  const condPath = validateConditions(body.conditions);
  if (condPath) return condPath;
  const actionPath = validateAction(body.action);
  if (actionPath) return actionPath;
  return null;
}

export const discountRulesHandlers = [
  http.get(ROOT, ({ request }) => {
    const url = new URL(request.url);
    const promotionId = url.searchParams.get('promotion_id');
    let rows = rulesStore;
    if (promotionId) rows = rows.filter((r) => r.promotion_id === promotionId);
    return HttpResponse.json(paginate(rows, {
      page: url.searchParams.get('page'),
      perPage: url.searchParams.get('per_page') || 50,
    }));
  }),

  http.post(ROOT, async ({ request }) => {
    const body = await request.json();
    const path = validateRuleBody(body);
    if (path) {
      return engineError('rule_validation_failed', {
        status: 409,
        message: 'Rule failed schema validation.',
        path,
      });
    }
    const row = {
      id: `dr-${Date.now()}`,
      rule_version: 1,
      ...body,
    };
    rulesStore.push(row);
    return HttpResponse.json({ data: row }, { status: 201 });
  }),

  http.patch(`${ROOT}/:id`, async ({ params, request }) => {
    const body = await request.json();
    const idx = rulesStore.findIndex((r) => r.id === params.id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });

    const merged = { ...rulesStore[idx], ...body };
    const path = validateRuleBody(merged);
    if (path) {
      return engineError('rule_validation_failed', {
        status: 409,
        message: 'Rule failed schema validation.',
        path,
      });
    }
    rulesStore[idx] = { ...merged, rule_version: (rulesStore[idx].rule_version || 0) + 1 };
    return HttpResponse.json({ data: rulesStore[idx] });
  }),
];
